import dayjs from "dayjs";

// =============================================================================
// VERSION SIMPLIFIÉE : les répartitions sont pré-calculées côté SQL
// Plus besoin de clesRepartition ni de appliquerCleRepartition()
// =============================================================================

export const calculerDetailPenalite = (joursRetard, tranches) => {
    if (joursRetard <= 0 || !tranches || tranches.length === 0) {
        return { total: 0, details: [] };
    }

    const details = [];
    let total = 0;

    const tranchesTriees = [...tranches].sort((a, b) => a.joursDebut - b.joursDebut);

    for (const tranche of tranchesTriees) {
        if (joursRetard > tranche.joursDebut) {
            const joursApplicables =
                joursRetard <= tranche.joursFin
                    ? joursRetard - tranche.joursDebut
                    : tranche.joursFin - tranche.joursDebut;

            if (joursApplicables > 0) {
                const montantTranche = joursApplicables * tranche.montantParJour;

                details.push({
                    tranche: `${tranche.joursDebut}-${tranche.joursFin} jours`,
                    joursApplicables,
                    tarifParJour: tranche.montantParJour,
                    montantTranche
                });

                total += montantTranche;
            }
        }
    }

    return { total, details };
};


export const grouperCessionsParCompagnie = (cessions, dateLimiteCession, penalites, moisRetrocession) => {
    const groupes = new Map();

    cessions.forEach((cession) => {
        const compagnieCode = cession.compagnie.code;

        if (!groupes.has(compagnieCode)) {
            groupes.set(compagnieCode, {
                compagnie: cession.compagnie,
                dateCession: cession.dateEmission,
                cessions: [],
                totalEmissions: 0,
                totalAnnulations: 0,
                totalSinistres: 0,
                totalRecours: 0,
                joursRetard: 0,
                penalite: 0,
                detailsPenalite: null,
                emissions: [],
                annulations: [],
                sinistres: [],
                recours: [],
                // Nouveaux champs pré-calculés
                emissionsRetrocedees: [],
                totalEmissionsRetrocedees: 0,
            });
        }

        const groupe = groupes.get(compagnieCode);
        groupe.cessions.push(cession);

        if (cession.bordereauEmissions?.items) {
            cession.bordereauEmissions.items.forEach(item => {
                groupe.emissions.push({
                    ...item,
                    cessionId: cession.codeId,
                    dateCession: cession.dateEmission
                });
            });
        }
        groupe.totalEmissions += cession.totalEmissions || 0;

        if (cession.bordereauAnnulations?.items) {
            cession.bordereauAnnulations.items.forEach(item => {
                groupe.annulations.push({
                    ...item,
                    cessionId: cession.codeId,
                    dateCession: cession.dateEmission
                });
            });
        }
        groupe.totalAnnulations += cession.totalAnnulations || 0;

        if (cession.bordereauSinistresPayes?.items) {
            cession.bordereauSinistresPayes.items.forEach(item => {
                groupe.sinistres.push({
                    ...item,
                    cessionId: cession.codeId,
                    dateCession: cession.dateEmission
                });
            });
        }
        groupe.totalSinistres += cession.totalSinistres || 0;

        if (cession.bordereauRecoursEncaisses?.items) {
            cession.bordereauRecoursEncaisses.items.forEach(item => {
                groupe.recours.push({
                    ...item,
                    cessionId: cession.codeId,
                    dateCession: cession.dateEmission
                });
            });
        }
        groupe.totalRecours += cession.totalRecours || 0;

        if (dateLimiteCession && cession.dateEmission) {
            const dateCess = dayjs(cession.dateEmission);
            const dateLimite = dayjs(dateLimiteCession);
            const retard = dateCess.diff(dateLimite, 'day');

            if (retard > 0) {
                groupe.joursRetard = Math.max(groupe.joursRetard, retard);
                groupe.detailsPenalite = calculerDetailPenalite(retard, penalites);
                groupe.penalite = groupe.detailsPenalite?.total || 0;
            }
        }
    });

    // Pré-calcul des émissions rétrocédées pour chaque compagnie
    const resultats = Array.from(groupes.values());
    resultats.forEach((groupe) => {
        // CORRECTION 1 : passer `groupe` (qui a .emissions), pas `groupe.compagnie`
        // CORRECTION 2 : utiliser `moisRetrocession`, pas `groupe.dateCession`
        const { emissionsRetrocedees } = traiterEmissionsCompagnie(groupe, moisRetrocession);
        groupe.emissionsRetrocedees = emissionsRetrocedees;
        groupe.totalEmissionsRetrocedees = emissionsRetrocedees.reduce(
            (sum, e) => sum + (e.montantOriginal || 0), 0
        );
    });

    return resultats;
};


export const appliquerCleRepartition = (montant, dateReference, compagnieCode, clesRepartition) => {
    const cleApplicable = clesRepartition.find(cle =>
        cle.compagnie.code === compagnieCode &&
        dayjs(dateReference).isBetween(dayjs(cle.dateEffet), dayjs(cle.dateEcheance), null, '[]')
    );

    if (!cleApplicable) {
        console.warn(`Aucune clé de répartition trouvée pour ${compagnieCode} à la date ${dateReference}`);
        return { montantRetrocede: 0, tauxApplique: 0 };
    }

    return {
        montantRetrocede: montant * (cleApplicable.taux / 100),
        tauxApplique: cleApplicable.taux
    };
};

// Fonction pour extraire le montant rétrocédé pour une compagnie spécifique
export const getMontantRetrocedeParCompagnie = (item, compagnieCode) => {
    if (!item.repartition || !Array.isArray(item.repartition)) {
        return 0;
    }
    const repartitionCompagnie = item.repartition.find(r => r.compagnieCode === compagnieCode);
    return repartitionCompagnie ? repartitionCompagnie.montant : 0;
};

export const getTauxRetrocedeParCompagnie = (item, compagnieCode) => {
    if (!item.repartition || !Array.isArray(item.repartition)) {
        return 0;
    }
    const repartitionCompagnie = item.repartition.find(r => r.compagnieCode === compagnieCode);
    return repartitionCompagnie ? repartitionCompagnie.taux : 0;
};

// =============================================================================
// FONCTIONS DE TRAITEMENT SIMPLIFIÉES
// Les répartitions sont déjà dans chaque item (repartition + totalRetrocede)
// Plus besoin du paramètre clesRepartition
// =============================================================================

export const traiterEmissionsCompagnie = (compagnie, moisRetrocession) => {
    const emissionsRetrocedees = [];
    const emissionsReportees = [];

    compagnie?.emissions?.forEach(emission => {
        const anneeEffet = dayjs(emission.effet).year();
        const anneeCession = dayjs(moisRetrocession).year();
        console.log("AC: ", anneeCession, " MR: ", moisRetrocession);
        console.log("IvresseEmissions", emission);

        if (anneeEffet <= anneeCession) {
            emissionsRetrocedees.push({
                ...emission,
                montantOriginal: emission.primeACeder,
                // totalRetrocede est déjà calculé côté SQL
                montantRetrocede: emission.totalRetrocede || 0,
                compagnie: compagnie.compagnie
            });
            console.log("Oui", emissionsRetrocedees);
        } else {
            emissionsReportees.push(emission);
        }
    });

    return { emissionsRetrocedees, emissionsReportees };
};

export const traiterAnnulationsCompagnie = (compagnie, moisRetrocession) => {
    return compagnie.annulations.map(annulation => ({
        ...annulation,
        montantOriginal: Math.abs(annulation.primeACeder),
        // totalRetrocede est déjà calculé côté SQL
        montantRetrocede: annulation.totalRetrocede || 0,
        compagnie: compagnie.compagnie
    }));
};

export const traiterSinistresCompagnie = (compagnie, moisRetrocession) => {
    return compagnie.sinistres.map(sinistre => ({
        ...sinistre,
        montantOriginal: sinistre.montant,
        // totalRetrocede est déjà calculé côté SQL
        montantRetrocede: sinistre.totalRetrocede || 0,
        compagnie: compagnie.compagnie
    }));
};

export const traiterRecoursCompagnie = (compagnie, moisRetrocession) => {
    return compagnie.recours.map(recours => ({
        ...recours,
        montantOriginal: recours.montant,
        // totalRetrocede est déjà calculé côté SQL
        montantRetrocede: recours.totalRetrocede || 0,
        compagnie: compagnie.compagnie
    }));
};

// =============================================================================
// RÉCAPITULATIF GLOBAL (simplifié, plus de clesRepartition)
// =============================================================================

export const calculerRecapitulatifGlobal = (cessionsParCompagnie, moisRetrocession) => {
    let totalEmissions = 0;
    let totalAnnulations = 0;
    let totalSinistres = 0;
    let totalRecours = 0;
    let totalPenalites = 0;

    cessionsParCompagnie.forEach(compagnie => {
        const { emissionsRetrocedees } = traiterEmissionsCompagnie(compagnie, moisRetrocession);
        const annulationsRetrocedees = traiterAnnulationsCompagnie(compagnie, moisRetrocession);
        const sinistresRetrocedes = traiterSinistresCompagnie(compagnie, moisRetrocession);
        const recoursRetrocedes = traiterRecoursCompagnie(compagnie, moisRetrocession);

        totalEmissions += emissionsRetrocedees.reduce((sum, e) => sum + (e.montantRetrocede || 0), 0);
        totalAnnulations += annulationsRetrocedees.reduce((sum, a) => sum + (a.montantRetrocede || 0), 0);
        totalSinistres += sinistresRetrocedes.reduce((sum, s) => sum + (s.montantRetrocede || 0), 0);
        totalRecours += recoursRetrocedes.reduce((sum, r) => sum + (r.montantRetrocede || 0), 0);
        totalPenalites += compagnie.penalite || 0;
    });

    return {
        totalEmissions,
        totalAnnulations,
        totalSinistres,
        totalRecours,
        totalPenalites,
        solde: (totalEmissions + totalRecours) - (totalAnnulations + totalSinistres) + totalPenalites
    };
};